import { UserPermissions } from '@zspace/roles';
import {
  DeferredResponse,
  PaginatedAPIResponse,
  PaginatedContentConfig,
  SalesOrdersAccessTableRow,
  SortDirection,
  TemporaryAccessSalesOrdersCriteria,
} from '@zspace/types';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Element, Icon } from 'react-bulma-components';
import { FaArrowRight } from 'react-icons/fa6';
import {
  defer,
  LoaderFunction,
  useAsyncValue,
  useLoaderData,
  useNavigate,
  useNavigation,
  useOutletContext,
  useSearchParams,
} from 'react-router-dom';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import FilterBar from '../../shared/filter-bar/filter-bar';
import useUpdateUrl from '../../shared/hooks/url';
import ProtectedPage from '../../shared/protected-page/protected-page';
import { createSearchParams } from '../../shared/url';
import Button from '../../ui/button/button';
import FeedbackMessage from '../../ui/feedback-message/feedback-message';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import SelectionIndicator from '../../ui/selection-indicator/selection-indicator';
import { OnTableChangeData, RowSelection } from '../../ui/table/types';
import { fetchAvailableSalesOrdersForTemporaryAccessGranting } from '../admin-service';
import AdminTemporaryAccessSalesOrdersTable from '../admin-temporary-access-sales-orders-table/admin-temporary-access-sales-orders-table';
import {
  TemporaryAccessSetupPageContext,
  TemporaryAccessSetupSteps,
} from '../admin-temporary-access-setup-page/types';
import { SalesOrdersAccessStorageService } from '../sales-orders-access-storage-service';

const NO_SALES_ORDERS_ERROR_MESSAGE =
  'You must select at least one Sales Order to grant access';

const customerAccessSalesOrdersSessionStorageService =
  new SalesOrdersAccessStorageService();

const initialCriteria = (searchParams?: URLSearchParams) => {
  return {
    itemsPerPage: parseInt(searchParams?.get('itemsPerPage') ?? '10'),
    pageNumber: parseInt(searchParams?.get('pageNumber') ?? '1'),
    search: searchParams?.get('search') ?? '',
    sortBy: searchParams?.get('sortBy') ?? 'number',
    sortDirection:
      (searchParams?.get('sortDirection') as SortDirection) ??
      SortDirection.DES,
    hardware: searchParams?.get('hardware') === 'true',
  };
};

export const loader: LoaderFunction = async ({ request }) => {
  const searchParams = createSearchParams(request);
  const criteria = initialCriteria(searchParams);

  const response =
    fetchAvailableSalesOrdersForTemporaryAccessGranting(criteria);

  return defer({ response });
};

export function AdminCustomerAccessSalesOrdersPageContent() {
  const navigate = useNavigate();
  const navigation = useNavigation();
  const [searchParams] = useSearchParams();
  const response =
    useAsyncValue() as PaginatedAPIResponse<SalesOrdersAccessTableRow>;
  const updateUrl = useUpdateUrl();
  const { setStep, setHeaderButton } =
    useOutletContext<TemporaryAccessSetupPageContext>();
  const [criteria, setCriteria] = useState<TemporaryAccessSalesOrdersCriteria>(
    initialCriteria(searchParams)
  );
  const [selectedSalesOrders, setSelectedSalesOrders] = useState<
    Map<string, SalesOrdersAccessTableRow>
  >(
    () =>
      new Map(
        customerAccessSalesOrdersSessionStorageService
          .get()
          .map((item) => [item.id, item])
      )
  );
  const [nextButtonPressed, setNextButtonPressed] = useState<boolean>(false);

  const tableData = useMemo(() => response.data, [response.data]);

  const tableDataMap = useMemo(
    () => new Map(tableData.map((item) => [item.id, item])),
    [tableData]
  );

  const tableConfig: PaginatedContentConfig = useMemo(() => {
    return {
      ...criteria,
      pages: response.pages,
    };
  }, [criteria, response.pages]);

  const customizeViewOptions = useMemo(
    () => [
      {
        title: 'Only show SO with hardware',
        applied: !!criteria.hardware,
        valueKey: 'hardware',
      },
    ],
    [criteria.hardware]
  );

  const rowSelection: RowSelection = useMemo(() => {
    return {
      onChange: (selectedRowIds) => {
        selectedRowIds.forEach((id) => {
          setSelectedSalesOrders((oldSelectedSalesOrders) => {
            const newSelectedSalesOrders = new Map(oldSelectedSalesOrders);
            if (oldSelectedSalesOrders?.has(id)) {
              newSelectedSalesOrders.delete(id);
            } else {
              const newSalesOrder = tableDataMap.get(id);
              newSelectedSalesOrders.set(id, newSalesOrder!);
            }
            customerAccessSalesOrdersSessionStorageService.save(
              Array.from(newSelectedSalesOrders.values())
            );
            return newSelectedSalesOrders;
          });
        });
      },
      selectedRowKeys: tableData
        .filter((v) => Array.from(selectedSalesOrders.keys()).includes(v.id))
        .map((v) => v.id),
    };
  }, [selectedSalesOrders, tableData, tableDataMap]);

  const hasSalesOrdersSelected = useMemo(
    () => selectedSalesOrders.size > 0,
    [selectedSalesOrders]
  );

  const showMissingSalesOrdersError = useMemo(
    () => !hasSalesOrdersSelected && nextButtonPressed,
    [hasSalesOrdersSelected, nextButtonPressed]
  );

  const handleOnTableChange = useCallback(
    (value: OnTableChangeData<SalesOrdersAccessTableRow>) => {
      const newCriteria = {
        ...criteria,
        ...value.config,
        sortBy: value.column?.key || criteria.sortBy,
      };

      setCriteria(newCriteria);
      updateUrl(newCriteria);
    },
    [criteria, updateUrl]
  );

  const handleTableFilterSubmit = useCallback(
    (tableFilterValue: TemporaryAccessSalesOrdersCriteria): void => {
      const newCriteria = { ...criteria, ...tableFilterValue, pageNumber: 1 };
      setCriteria(newCriteria);
      updateUrl(newCriteria);
    },
    [criteria, updateUrl]
  );

  const onRemoveFilters = useCallback(
    (filterDataKeys: (keyof TemporaryAccessSalesOrdersCriteria)[]) => {
      const newCriteria: TemporaryAccessSalesOrdersCriteria = {
        ...criteria,
        pageNumber: 1,
      };
      for (const filterDataKey of filterDataKeys) {
        const initialFilterValue = initialCriteria()[filterDataKey];
        if (initialFilterValue === undefined) {
          delete newCriteria[filterDataKey];
        } else {
          newCriteria[filterDataKey] = initialFilterValue as string &
            string[] &
            number;
        }
      }
      setCriteria(newCriteria);
      updateUrl(newCriteria);
    },
    [criteria, updateUrl]
  );

  const emptyContentText = useMemo(() => {
    const hasAppliedFilters = criteria.search !== '';
    if (hasAppliedFilters) {
      return 'There are no sales orders that match the selected criteria';
    }

    return 'There are no sales orders to select at the moment';
  }, [criteria.search]);

  const isDataLoading = useMemo(
    () => navigation.state === 'loading',
    [navigation.state]
  );

  const onSelectUserClick = useCallback(() => {
    if (hasSalesOrdersSelected) {
      navigate('/admin/customer-access/setup/user');
    } else {
      setNextButtonPressed(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [hasSalesOrdersSelected, navigate]);

  useEffect(() => {
    const nextStepButton = (
      <Button color="primary-dark" onClick={onSelectUserClick}>
        <span>Select user</span>
        <Icon>
          <FaArrowRight />
        </Icon>
      </Button>
    );

    setStep(TemporaryAccessSetupSteps.SALES_ORDERS);
    setHeaderButton(nextStepButton);
  }, [onSelectUserClick, setHeaderButton, setStep]);

  if (isDataLoading) {
    return (
      <Element display="flex" justifyContent="center">
        <PageSpinner />
      </Element>
    );
  }

  return (
    <>
      <Element display="flex" className="w-full" alignItems="center">
        <FilterBar
          initialData={initialCriteria()}
          data={criteria}
          onFilterSubmit={handleTableFilterSubmit}
          onRemoveFilter={onRemoveFilters}
          customizeViewOptions={customizeViewOptions}
        />
      </Element>

      <AdminTemporaryAccessSalesOrdersTable
        dataSource={tableData}
        config={tableConfig}
        onChange={handleOnTableChange}
        empty={
          <Element display="flex" justifyContent="center" className="my-2">
            <span className="has-text-weight-light">{emptyContentText}</span>
          </Element>
        }
        rowSelection={rowSelection}
        errorMessage={
          showMissingSalesOrdersError && (
            <Element mb={3}>
              <FeedbackMessage>{NO_SALES_ORDERS_ERROR_MESSAGE}</FeedbackMessage>
            </Element>
          )
        }
        form={{
          leftExtra: (
            <SelectionIndicator
              selected={selectedSalesOrders.size}
              total={response.total}
            />
          ),
        }}
      />
    </>
  );
}

export function AdminCustomerAccessSalesOrdersPage() {
  const { response } = useLoaderData() as DeferredResponse<
    Promise<PaginatedAPIResponse<SalesOrdersAccessTableRow>>
  >;

  return (
    <ProtectedPage permissions={UserPermissions.USERS_SELF_ASSIGN_ROLES}>
      <Suspense fallback={<PageSpinner />}>
        <ErrorHandlingAwait resolve={response}>
          <AdminCustomerAccessSalesOrdersPageContent />
        </ErrorHandlingAwait>
      </Suspense>
    </ProtectedPage>
  );
}

export default AdminCustomerAccessSalesOrdersPage;
